'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@workspace/lib';

export const typographyCva = cva('', {
  variants: {
    variant: {
      h1: 'font-sora font-bold text-4xl md:text-6xl leading-12 md:leading-19.5',
      h2: 'font-sora font-bold text-[28px] md:text-5xl leading-9 md:leading-15',
      h3: 'font-sora font-bold text-2xl md:text-4xl leading-7.5 md:leading-12',
      h4: 'font-sora font-bold text-2xl md:text-[28px] leading-7.5 md:leading-9',
      h5: 'font-sora font-semibold text-lg md:text-2xl leading-6 md:leading-7.5',
      h6: 'font-sora font-bold text-lg md:text-lg leading-6',
      bodyLg: 'font-openSans font-normal text-xl md:text-xl leading-6 md:leading-8',
      bodyMd: 'font-openSans font-normal text-base md:text-base leading-5 md:leading-6',
      bodySm: 'font-openSans font-normal text-sm md:text-sm leading-4  md:leading-5',
      bodyXs: 'font-openSans font-normal text-xs md:text-xs leading-4',
      link: 'font-openSans font-semibold text-sm underline cursor-pointer',
      button: 'font-openSans font-semibold text-base',
      hyperlinkVisited: 'font-openSans font-semibold text-sm underline cursor-pointer',
    },
    tone: {
      default: 'text-secondary-400',
      link: 'text-primary',
      linkVisited: 'text-primary-500',
    },
    loading: {
      true: 'animate-pulse bg-secondary-50 text-transparent rounded-md',
      false: '',
    },
  },
  compoundVariants: [],
  defaultVariants: { tone: 'default', loading: false },
});

type TVariants = VariantProps<typeof typographyCva>;
export type TypographyVariant = NonNullable<TVariants['variant']>;

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant: TypographyVariant;
  children: React.ReactNode;
  className?: string;
  href?: string;
  as?: React.ElementType;
  onClick?: (e: React.MouseEvent) => void;
  fontWeight?: string;
  textColor?: string;
  style?: React.CSSProperties;
  loading?: boolean;
  styleVariant?: TypographyVariant;
}

export function Typography({
  variant,
  children,
  className,
  href,
  as,
  onClick,
  fontWeight,
  textColor,
  style,
  loading = false,
  styleVariant,
  ...rest
}: TypographyProps) {
  const tone: TVariants['tone'] =
    variant === 'link' ? 'link' : variant === 'hyperlinkVisited' ? 'linkVisited' : 'default';

  // Use styleVariant for styling if provided, otherwise use variant
  const styleVariantToUse = styleVariant || variant;
  const classes = typographyCva({ variant: styleVariantToUse, tone, loading });
  const merged = cn(classes, fontWeight, className);
  const commonProps = {
    onClick,
    className: merged,
    style: textColor ? { ...style, color: textColor } : style,
    ...rest,
  };

  const defaultTag: Record<TypographyVariant, React.ElementType> = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    bodyLg: 'p',
    bodyMd: 'p',
    bodySm: 'p',
    bodyXs: 'p',
    link: href ? 'a' : 'span',
    hyperlinkVisited: href ? 'a' : 'span',
    button: 'span',
  };

  const Component = as ?? defaultTag[variant] ?? 'span';

  if (variant === 'link' || variant === 'hyperlinkVisited') {
    if (href) {
      return (
        <a href={href} {...commonProps}>
          {children}
        </a>
      );
    }
    return <Component {...commonProps}>{children}</Component>;
  }

  return <Component {...commonProps}>{children}</Component>;
}
